import React from 'react'
import { Drawer, Empty, Tag, Timeline } from 'antd'
import moment from 'moment'
import styled from 'styled-components/macro'
import { getInvoiceReconciliationMethodLabel } from '../../utils/invoiceUploadHelpers'
import { getInvoiceMatchedOrdersTableDisplay } from '../../utils/invoiceMatchedOrdersDisplayHelpers'

const AUDIT_EVENT_LABELS = {
  invoice_credit_requested: 'Supplier credit requested',
  invoice_credit_discrepancy_status_changed: 'Credit discrepancy updated',
  invoice_difference_accepted: 'Difference accepted & reconciled',
  invoice_uploaded: 'Invoice uploaded',
  invoice_ocr_processed: 'Invoice read by OMS',
  invoice_enriched: 'Invoice details confirmed',
  invoice_match_evaluated: 'Smart match evaluated',
  invoice_linked_to_po: 'Linked to purchase order',
  invoice_unlinked_from_po: 'Removed from purchase order',
  invoice_reconciled_one_click: 'Reconciled with Smart Match',
  invoice_reconciled_force: 'Force reconciled',
  invoice_reconcile_processing_failed: 'Reconciliation failed',
  invoice_allocations_saved: 'PO allocations saved',
  invoice_details_edited: 'Invoice details edited',
  invoice_pages_merged: 'Invoice pages merged',
  invoice_duplicate_dismissed: 'Duplicate dismissed',
  receipt_retrospectively_edited: 'Receipt retrospectively edited',
  supplier_discount_corrected: 'Supplier discount corrected',
  xero_invoice_exported: 'Invoice exported to Xero',
  xero_credit_note_exported: 'Credit Note exported to Xero',
  xero_credit_allocated: 'Credit allocated against Xero Bill',
  xero_export_failed: 'Xero export failed',
}

function getAuditEventLabel(event) {
  return (
    AUDIT_EVENT_LABELS[event] ||
    String(event || 'Invoice updated')
      .replace(/^invoice_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase())
  )
}

function getAuditColor(event) {
  if (
    event === 'invoice_reconcile_processing_failed' ||
    event === 'xero_export_failed'
  ) {
    return 'red'
  }
  if (
    event === 'receipt_retrospectively_edited' ||
    event === 'supplier_discount_corrected'
  ) {
    return 'orange'
  }
  if (
    String(event || '').includes('reconciled') ||
    event === 'invoice_difference_accepted' ||
    event === 'xero_invoice_exported' ||
    event === 'xero_credit_note_exported' ||
    event === 'xero_credit_allocated'
  ) {
    return 'green'
  }
  if (event === 'invoice_match_evaluated') return 'blue'
  if (event === 'invoice_details_edited') return 'orange'
  return 'gray'
}

function formatAuditChangeValue(value) {
  if (value == null || value === '') return '—'
  return String(value)
}

function FieldChanges({ changes }) {
  const entries = Object.entries(changes || {})
  if (!entries.length) return null

  return (
    <ChangesList>
      {entries.map(([field, change]) => (
        <li key={field}>
          <code>{field}</code>
          <span>
            {formatAuditChangeValue(change?.from)} →{' '}
            {formatAuditChangeValue(change?.to)}
          </span>
        </li>
      ))}
    </ChangesList>
  )
}

function AllocationSnapshot({ snapshot = {} }) {
  const pos = Array.isArray(snapshot.purchaseOrderNumbers)
    ? snapshot.purchaseOrderNumbers.filter(Boolean)
    : []
  if (!pos.length) return null

  return (
    <AllocationList>
      {pos.map(po => (
        <li key={po}>{po}</li>
      ))}
    </AllocationList>
  )
}

function InvoiceAuditHistoryDrawer({ open, invoice, onClose }) {
  const history = [...(invoice?.auditHistory || [])].sort(
    (left, right) =>
      new Date(right?.timestamp || 0).getTime() -
      new Date(left?.timestamp || 0).getTime(),
  )
  const matchedOrders = getInvoiceMatchedOrdersTableDisplay(invoice || {})

  return (
    <Drawer
      visible={open}
      title={`Invoice history${
        invoice?.invoiceNumber ? ` — ${invoice.invoiceNumber}` : ''
      }`}
      width={520}
      onClose={onClose}
      destroyOnClose
    >
      <MethodSummary>
        <span>Reconciliation method</span>
        <Tag color={invoice?.reconciliationMethod ? 'green' : 'default'}>
          {getInvoiceReconciliationMethodLabel(invoice?.reconciliationMethod)}
        </Tag>
        {invoice?.reconciledByName ? (
          <small>by {invoice.reconciledByName}</small>
        ) : null}
      </MethodSummary>

      {matchedOrders.isMultiPo ? (
        <MatchedOrdersSummary>
          <strong>{matchedOrders.label}</strong>
          <span>{matchedOrders.subtitle}</span>
        </MatchedOrdersSummary>
      ) : null}

      {history.length ? (
        <Timeline>
          {history.map((entry, index) => (
            <Timeline.Item
              color={getAuditColor(entry.event)}
              key={`${entry.event}-${entry.timestamp || index}`}
            >
              <EventHeader>
                <strong>{getAuditEventLabel(entry.event)}</strong>
                <time>
                  {entry.timestamp
                    ? moment(entry.timestamp).format('DD MMM YYYY, HH:mm')
                    : 'Time unavailable'}
                </time>
              </EventHeader>
              {entry.detail ? <Detail>{entry.detail}</Detail> : null}
              {entry.event === 'invoice_allocations_saved' &&
              entry?.dataSnapshot ? (
                <AllocationSnapshot snapshot={entry.dataSnapshot} />
              ) : null}
              {entry?.dataSnapshot?.changes ? (
                <FieldChanges changes={entry.dataSnapshot.changes} />
              ) : null}
              {entry.actor ? (
                <Actor>
                  {entry.actor}
                  {entry.actorType ? ` · ${entry.actorType}` : ''}
                </Actor>
              ) : null}
              {entry.dataSnapshot ? (
                <Snapshot>
                  <summary>Technical details</summary>
                  <pre>{JSON.stringify(entry.dataSnapshot, null, 2)}</pre>
                </Snapshot>
              ) : null}
            </Timeline.Item>
          ))}
        </Timeline>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No audit events have been recorded for this invoice yet.'
        />
      )}
    </Drawer>
  )
}

export default InvoiceAuditHistoryDrawer

const MethodSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  margin-bottom: 24px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;

  span {
    font-weight: 600;
  }

  small {
    color: #8c8c8c;
  }
`

const EventHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  time {
    color: #8c8c8c;
    font-size: 12px;
  }
`

const Detail = styled.p`
  margin: 6px 0 0;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.45;
  white-space: pre-wrap;
`

const MatchedOrdersSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;

  span {
    color: rgba(0, 0, 0, 0.65);
    font-size: 13px;
    line-height: 1.45;
  }
`

const AllocationList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  color: rgba(0, 0, 0, 0.75);
  font-size: 13px;
`

const ChangesList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
  }

  code {
    font-size: 12px;
    color: #595959;
  }
`

const Actor = styled.p`
  margin: 4px 0 0;
  color: #8c8c8c;
  font-size: 12px;
`

const Snapshot = styled.details`
  margin-top: 8px;
  font-size: 12px;

  summary {
    color: #1890ff;
    cursor: pointer;
  }

  pre {
    max-height: 220px;
    overflow: auto;
    padding: 8px;
    border-radius: 4px;
    background: #f5f5f5;
    white-space: pre-wrap;
    word-break: break-word;
  }
`
